import { IGameState } from '../IGameState';
import { AsyncActionStatus } from '../../AsyncAction';
import { ThunkAction } from 'redux-thunk';
import { Action } from 'typesafe-actions';
import { DataConflictActions } from '../DataConflictActions';
import { fetchDataConflicts } from '@platform/Utility/SDK/DataConflict/fetchDataConflicts';
import { PopupID } from '../../UI/PopupID';
import Game from '../../../Game';
import UIStateHelper from 'State/UI/UIStateHelper';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { IRewardData } from 'Utility/RewardHelper';
import { report } from 'report';
import {
    DataConflictAutoResolveReason,
    autoResolveDataSetsIfPossible,
    getRestoreSelection,
    sortDataLocationSets,
    updateLostDataPropertyInChosenSet,
} from 'Utility/DataConflict/DataConflictsHelper';
import { track } from 'Utility/Tracking/Track';

export function fetchConflicts(): ThunkAction<Promise<void>, IGameState, never, Action> {
    return async (dispatch, getState) => {
        const { status } = getState().dataConflict;
        if (status !== AsyncActionStatus.COMPLETE && status !== AsyncActionStatus.EMPTY) {
            return;
        }
        DataConflictActions.startFetchingConflicts();

        const dataSets = await fetchDataConflicts();
        if (!dataSets) {
            DataConflictActions.failFetchingPlayerData();
            return;
        }

        DataConflictActions.setConflicts(dataSets);

        dispatch(DataConflictActions.attemptDataConflictAutoResolve());

        const { status: autoResolveStatus } = getState().dataConflict;
        const wasAutoResolveSuccessful = autoResolveStatus === AsyncActionStatus.COMPLETE;

        if (!wasAutoResolveSuccessful) {
            DataConflictActions.promptConflictResolution();
        }
    };
}

export function attemptDataConflictAutoResolve(): ThunkAction<Promise<void>, IGameState, never, Action> {
    return async (dispatch, getState) => {
        const { result: dataSets } = getState().dataConflict;

        const autoResolvedSets = autoResolveDataSetsIfPossible(dataSets);
        if (autoResolvedSets.length === 1) {
            let resolvedSet = autoResolvedSets[0];
            trackAutoResolve(resolvedSet.autoResolveReason);

            const restoreSelection = getRestoreSelection(dataSets, resolvedSet);

            resolvedSet = updateLostDataPropertyInChosenSet(dataSets, resolvedSet);

            await Game.sdk.save(resolvedSet.playerData);
            await Game.sdk.flushSaveData();

            Game.player.data.playerEndpointData = resolvedSet.playerData;

            DataConflictActions.resolveConflicts({
                data: resolvedSet.playerData,
                restoreSelection: restoreSelection,
                playerDataLocation: resolvedSet.location,
            });
        }
    };
}

function trackAutoResolve(reason: DataConflictAutoResolveReason): void {
    track('dataConflictAutoResolved', 1);
    track('dataConflictAutoResolveReason', reason);
}

export function startFetching(state: IGameState): IGameState {
    return {
        ...state,
        dataConflict: {
            ...state.dataConflict,
            status: AsyncActionStatus.QUEUED,
        },
    };
}

export function failedFetchingPlayerData(state: IGameState): IGameState {
    report('Failed to fetch player data', {
        once: true,
        sampleRate: 0.1,
        fingerprint: 'DataConflict:failedFetchingPlayerData',
    });
    return {
        ...state,
        dataConflict: {
            status: AsyncActionStatus.FAILED,
            result: [],
        },
        UIState: UIStateHelper.showPopup(state.UIState, PopupID.ERROR_POPUP, {
            title: Game.locale.getText('ConnectionError'),
            body: Game.locale.getText('CantFetchFacebookData'),
            disallowClose: true,
            submitAction: () => DataConflictActions.acceptFailFetchingPlayerData(),
        }),
    };
}

export function acceptFailedFetchingConflicts(state: IGameState): IGameState {
    return {
        ...state,
        gameLoaded: true,
        gamePaused: false,
        UIState: UIStateHelper.hidePopup(state.UIState, PopupID.ERROR_POPUP),
    };
}

export function completeConflictResolution(
    state: IGameState,
    status: AsyncActionStatus = AsyncActionStatus.COMPLETE
): IGameState {
    return {
        ...state,
        gamePaused: false,
        playerState: {
            ...state.playerState,
        },
        dataConflict: {
            status,
            result: [],
        },
    };
}

export function completeFacebookReward(state: IGameState): IGameState {
    const rewards: IRewardData[] = state.platform.facebookSignInReward.result;
    return {
        ...state,
        playerState: InventoryStateHelper.receiveRewards(state.playerState, rewards, {
            dontTrack: true,
            queueTask: true,
        }),
        platform: {
            ...state.platform,
            facebookSignInReward: {
                status: AsyncActionStatus.COMPLETE,
                result: [],
            },
        },
    };
}

export function promptConflictResolution(state: IGameState): IGameState {
    const sortedDataSets = sortDataLocationSets(state.dataConflict.result);
    state = UIStateHelper.showPopupFullState(state, PopupID.DATA_CONFLICT_POPUP, {
        dataSets: sortedDataSets,
    });

    return {
        ...state,
        gamePaused: true,
        dataConflict: {
            status: AsyncActionStatus.IN_PROGRESS,
            result: sortedDataSets,
        },
    };
}
